/**
 * `contra` 明细的**错误态判定**（批 `111` 从抽屉里抽出）
 *
 * ## 🔴 为什么单独抽成纯函数
 *
 * 「不存在的 `contraId` ⇒ 明确的空态/错误，**不是 500**」这条验收要能在端侧取证。
 * 但错误态是 `useQuery` 的**异步分支** —— 静态渲染（`renderToStaticMarkup`）
 * 只跑一次同步渲染，永远停在 `isPending`，**看不到 `isError`**（`test/render.tsx` §能测什么）。
 *
 * ⇒ 把"给定错误该显示什么"抽成纯函数：判定可**直接测**，组件只负责把结果画出来。
 * ⚠️ 诚实边界：这证的是"文案与严重级判定正确"，**不证**"Promise 失败后真的会走到这一支"
 * —— 后者靠真机 + 服务端 404 的通路（冒烟覆盖）。
 *
 * ## 文案从哪来
 *
 * 🔴 服务端的 `message` 是**给人看的中文**（`api/http.ts` 文件头）⇒ 原样展示，端侧不编
 * （编一遍 = 把服务端口径又写一份，会漂移）。只有 **404** 例外：服务端那句
 * 「账户不存在或不属于当前账本」对用户不够可操作，端侧补一句**下一步**。
 */

import { ApiError } from '../api/http.js';

export interface TimelineErrorView {
  /** `warning` = 用户能自己纠正（地址错 / 账户类型不对）；`error` = 真出错了 */
  severity: 'warning' | 'error';
  text: string;
}

export function timelineErrorView(error: unknown): TimelineErrorView {
  if (error instanceof ApiError) {
    if (error.status === 404) {
      return {
        severity: 'warning',
        text: '没有找到这个往来账户 —— 可能已被删除，或者不属于当前账本。回到「债权债务」重新选一个。',
      };
    }
    // 400 = 这个账户不是借贷账户（服务端原文已说清原因）；其余 4xx 同理
    if (error.status >= 400 && error.status < 500) {
      return { severity: 'warning', text: error.message };
    }
    return { severity: 'error', text: `明细读取失败：${error.message}` };
  }
  return { severity: 'error', text: '明细读取失败（网络异常，请稍后重试）。' };
}
